import { useQuery } from '@tanstack/react-query'
import { Spinner, Text } from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { trpc, trpcClient } from '../../../utils/trpc'
import { DashboardFilters, type DashboardFiltersValue } from './components/DashboardFilters'
import { buildCsvFromRows, EmployeeTable } from './components/EmployeeTable'
import { GoalCompletionByDepartmentChart } from './components/GoalCompletionByDepartmentChart'
import { HighPerformerByManagerChart } from './components/HighPerformerByManagerChart'
import { KPICard } from './components/KPICard'
import { RatingByDepartmentChart } from './components/RatingByDepartmentChart'
import { RatingDistributionChart } from './components/RatingDistributionChart'
import { ThreeSixtyVsPerformanceScatter } from './components/ThreeSixtyVsPerformanceScatter'
import { UnavailableModulePanel } from './components/UnavailableModulePanel'

const EMPTY_FILTERS: DashboardFiltersValue = {
	cycleIds: [],
	departments: [],
	businessUnits: [],
	locations: [],
	managerIds: [],
	performanceCategories: [],
	ratingMin: undefined,
	ratingMax: undefined,
}

function downloadCsv(filename: string, csv: string) {
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

function downloadBase64(filename: string, base64: string, contentType: string) {
	const byteString = atob(base64)
	const bytes = new Uint8Array(byteString.length)
	for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i)
	const blob = new Blob([bytes], { type: contentType })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

const EMPLOYEE_TABLE_PAGE_SIZE = 100

export function LeadershipDashboardPage() {
	const [filters, setFilters] = useState<DashboardFiltersValue>(EMPTY_FILTERS)
	const [isExporting, setIsExporting] = useState(false)
	const [isExportingExcel, setIsExportingExcel] = useState(false)
	const [pdfJobId, setPdfJobId] = useState<string | null>(null)
	const [pdfStatus, setPdfStatus] = useState<'idle' | 'pending' | 'processing' | 'completed' | 'failed'>('idle')
	const [employeePage, setEmployeePage] = useState(0)

	const setFiltersAndResetPage = (next: DashboardFiltersValue) => {
		setFilters(next)
		setEmployeePage(0)
	}

	const initQuery = useQuery(trpc.client.leadershipDashboard.initialize.queryOptions())

	const effectiveFilters = useMemo(() => {
		const init = initQuery.data
		if (!init) return filters
		if (filters.cycleIds.length > 0) return filters
		return init.defaultCycleId ? { ...filters, cycleIds: [init.defaultCycleId] } : filters
	}, [filters, initQuery.data])

	const queryInput = { filters: effectiveFilters }
	const enabled = !!initQuery.data && effectiveFilters.cycleIds.length > 0

	const dashboardDataQuery = useQuery({
		...trpc.client.leadershipDashboard.getDashboardData.queryOptions(queryInput),
		enabled,
	})
	const employeeTableQuery = useQuery({
		...trpc.client.leadershipDashboard.getEmployeeTable.queryOptions({
			...queryInput,
			limit: EMPLOYEE_TABLE_PAGE_SIZE,
			offset: employeePage * EMPLOYEE_TABLE_PAGE_SIZE,
		}),
		enabled,
	})

	const pdfStatusQuery = useQuery({
		...trpc.client.leadershipDashboard.getPdfSnapshotStatus.queryOptions(
			pdfJobId ? { jobId: pdfJobId } : { jobId: '00000000-0000-0000-0000-000000000000' },
		),
		enabled: !!pdfJobId && (pdfStatus === 'pending' || pdfStatus === 'processing'),
		refetchInterval: 2000,
	})

	// Side effect, not memoization — useEffect fires once after the render where
	// the dependency changes; useMemo is allowed to re-run or skip its body
	// (notably under React Strict Mode), which would call window.open / setState
	// zero or two times per status transition.
	useEffect(() => {
		if (!pdfJobId) return
		const status = pdfStatusQuery.data?.status
		if (!status) return
		if (status === 'completed') {
			setPdfStatus('completed')
			const url = pdfStatusQuery.data?.downloadUrl
			if (url) window.open(url, '_blank', 'noopener,noreferrer')
		} else if (status === 'failed') {
			setPdfStatus('failed')
			window.alert('PDF export failed. Please try again.')
		} else if (status === 'processing') {
			setPdfStatus('processing')
		}
	}, [pdfJobId, pdfStatusQuery.data?.status, pdfStatusQuery.data?.downloadUrl])

	if (initQuery.isLoading) {
		return (
			<div data-testid='leadership-dashboard-loading' className='flex items-center justify-center h-64'>
				<Spinner />
			</div>
		)
	}

	if (initQuery.isError || !initQuery.data) {
		return (
			<div data-testid='leadership-dashboard-error' className='p-6'>
				<Text variant='small' className='text-red-600'>
					Couldn't load Leadership Dashboard. Please refresh the page and try again.
				</Text>
			</div>
		)
	}

	const init = initQuery.data
	const kpis = dashboardDataQuery.data?.kpis
	const moduleAvailability = init.moduleAvailability

	async function handleExportCsv() {
		if (!enabled) return
		setIsExporting(true)
		try {
			const result = await trpcClient.client.leadershipDashboard.exportEmployeeTable.mutate({
				filters: effectiveFilters,
			})
			if (result.total > result.rows.length) {
				const proceed = window.confirm(
					`This export is capped at ${result.rows.length.toLocaleString()} rows but the filtered set has ${result.total.toLocaleString()} matches. Download the partial CSV anyway?`,
				)
				if (!proceed) return
			}
			downloadCsv(`leadership-dashboard-${Date.now()}.csv`, buildCsvFromRows(result.rows))
		} catch (err) {
			console.error('Leadership Dashboard CSV export failed', err)
			window.alert("Couldn't export CSV. Please try again.")
		} finally {
			setIsExporting(false)
		}
	}

	async function handleExportExcel() {
		if (!enabled) return
		setIsExportingExcel(true)
		try {
			const result = await trpcClient.client.leadershipDashboard.exportExcel.mutate({
				filters: effectiveFilters,
			})
			downloadBase64(result.filename, result.data, result.contentType)
		} catch (err) {
			console.error('Leadership Dashboard Excel export failed', err)
			window.alert("Couldn't export Excel. Please try again.")
		} finally {
			setIsExportingExcel(false)
		}
	}

	async function handleRequestPdf() {
		if (!enabled) return
		try {
			setPdfStatus('pending')
			const result = await trpcClient.client.leadershipDashboard.requestPdfSnapshot.mutate({
				filters: effectiveFilters,
			})
			setPdfJobId(result.jobId)
		} catch (err) {
			console.error('Leadership Dashboard PDF export failed', err)
			window.alert("Couldn't start PDF export. Please try again.")
			setPdfStatus('failed')
		}
	}

	return (
		<div data-testid='leadership-dashboard' className='flex flex-col gap-6 p-6'>
			<div className='flex flex-col gap-2'>
				<Text variant='large' weight='semibold'>
					Leadership Dashboard
				</Text>
				<Text variant='small' className='text-neutral-500'>
					Executive view of performance ratings across review cycles.{' '}
					{init.isAdmin ? 'Admin scope.' : 'Manager scope: your direct + skip-level reports only.'}
				</Text>
			</div>

			<DashboardFilters
				value={effectiveFilters}
				options={init.availableFilters}
				onChange={setFiltersAndResetPage}
				defaults={{ cycleIds: init.defaultCycleId ? [init.defaultCycleId] : [] }}
			/>

			<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
				<KPICard
					title='Average Rating'
					value={kpis?.avgRating != null ? kpis.avgRating.toFixed(2) : null}
					subtitle={`${kpis?.totalEvaluated ?? 0} employees evaluated`}
					loading={dashboardDataQuery.isLoading}
					error={dashboardDataQuery.isError}
					dataTestid='kpi-avg-rating'
				/>
				<KPICard
					title='High Performer %'
					value={kpis?.highPerformerPercent != null ? kpis.highPerformerPercent.toFixed(1) : null}
					suffix='%'
					subtitle='Avg rating ≥ 4'
					loading={dashboardDataQuery.isLoading}
					error={dashboardDataQuery.isError}
					dataTestid='kpi-high-performer'
				/>
				<KPICard
					title='Participation %'
					tooltip='Completed evaluations divided by total evaluations in the selected cycle(s).'
					value={kpis?.participationPercent != null ? kpis.participationPercent.toFixed(1) : null}
					suffix='%'
					subtitle={`${kpis?.totalCompleted ?? 0} completed`}
					loading={dashboardDataQuery.isLoading}
					error={dashboardDataQuery.isError}
					dataTestid='kpi-participation'
				/>
				<KPICard
					title='Avg Goal Completion'
					value={kpis?.avgGoalCompletion != null ? kpis.avgGoalCompletion.toFixed(1) : null}
					suffix={kpis?.avgGoalCompletion != null ? '%' : undefined}
					subtitle={kpis?.goalsConfigured ? 'Goals configured' : 'Goals not configured'}
					tone={kpis?.goalsConfigured ? 'default' : 'na'}
					loading={dashboardDataQuery.isLoading}
					error={dashboardDataQuery.isError}
					dataTestid='kpi-goal-completion'
				/>
			</div>

			<div className='flex justify-end gap-2'>
				<button
					type='button'
					data-testid='leadership-dashboard-export-excel'
					onClick={handleExportExcel}
					disabled={!enabled || isExportingExcel}
					className='text-xs px-3 py-1.5 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed'>
					{isExportingExcel ? 'Exporting Excel…' : 'Export Excel'}
				</button>
				<button
					type='button'
					data-testid='leadership-dashboard-export-pdf'
					onClick={handleRequestPdf}
					disabled={!enabled || pdfStatus === 'pending' || pdfStatus === 'processing'}
					className='text-xs px-3 py-1.5 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed'>
					{pdfStatus === 'pending' || pdfStatus === 'processing'
						? 'Generating PDF…'
						: pdfStatus === 'completed'
							? 'PDF ready (re-export)'
							: 'Export PDF'}
				</button>
			</div>

			<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
				<div className='p-4 border border-neutral-200 rounded'>
					<RatingDistributionChart
						data={dashboardDataQuery.data?.chartData.distribution}
						loading={dashboardDataQuery.isLoading}
					/>
				</div>
				<div className='p-4 border border-neutral-200 rounded'>
					<RatingByDepartmentChart
						data={dashboardDataQuery.data?.chartData.ratingByDepartment}
						loading={dashboardDataQuery.isLoading}
					/>
				</div>
				<div className='p-4 border border-neutral-200 rounded'>
					<HighPerformerByManagerChart
						data={dashboardDataQuery.data?.chartData.highPerformerByManager}
						loading={dashboardDataQuery.isLoading}
					/>
				</div>
				<div className='p-4 border border-neutral-200 rounded'>
					{moduleAvailability.hasGoalsCycle ? (
						<GoalCompletionByDepartmentChart
							data={dashboardDataQuery.data?.goalCompletion.byDepartment}
							loading={dashboardDataQuery.isLoading}
						/>
					) : (
						<UnavailableModulePanel
							title='Goal Completion by Department'
							moduleLabel='Goals'
							dataTestid='goals-placeholder'
						/>
					)}
				</div>
				<div className='p-4 border border-neutral-200 rounded lg:col-span-2'>
					{moduleAvailability.hasThreeSixtyCycle ? (
						<ThreeSixtyVsPerformanceScatter
							data={dashboardDataQuery.data?.scatter}
							loading={dashboardDataQuery.isLoading}
						/>
					) : (
						<UnavailableModulePanel
							title='360 Score vs Performance Rating'
							moduleLabel='360 / Multi-Rater feedback'
							dataTestid='scatter-placeholder'
						/>
					)}
				</div>
			</div>

			<div className='p-4 border border-neutral-200 rounded'>
				<EmployeeTable
					rows={employeeTableQuery.data?.rows}
					total={employeeTableQuery.data?.total}
					loading={employeeTableQuery.isLoading}
					onExportCsv={handleExportCsv}
					exporting={isExporting}
					page={employeePage}
					pageSize={EMPLOYEE_TABLE_PAGE_SIZE}
					onPageChange={setEmployeePage}
				/>
			</div>
		</div>
	)
}
